<script lang="ts">
	//Services
	import { tick } from 'svelte';
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		ButtonV2,
		AlertV2,
		Modal,
		Container,
		Text,
		MenuItem,
		MainMenu,
		PillShapedDock,
		CardGrid,
		IconButton,
		ListNumberCard,
		Icon,
		toast,
	} from '@Shelly/shelly-design-system';

	import PreviewBox from 'src/components/PreviewBox.svelte';
	import { onStartDrag, onEndDrag } from 'src/components/Lists/list-helpers';
	import { getAppBackgroundSrc, hasBackgroundColorImage } from 'src/helpers/account_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { save_home } from 'src/helpers/home_helpers';
	import {
		appendMissingMenuOrderIndices,
		finalizeNewLayoutMenuOrder,
		menuItems,
		menuItemsNewLayout,
		partitionMainMenuOrderIndices,
		partitionNewLayoutMenuOrderIndices,
		parseHomeDataMenuOrderJson,
		MAIN_MENU_ORDER_INDEX_ADD_DEVICE,
		MORE_MENU_ICON,
		NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT,
	} from 'src/helpers/main_menu';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { home_data, profile_data } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	interface Item {
		id: string;
		isDndShadowItem?: boolean;
		index: number;
	}

	const flipDurationMs = 0;
	const MENU_ORDER_HINT_KEY = 'menu_order_first_item_closed';
	/** Matches MainMenuProvider / `.main-menu-indicator { left: 5px }`. */
	const PREVIEW_NAV_EDGE_INSET = 5;

	let showReorderMenuModal = false;
	let dragDisabled = true;
	let extractedItem: Item | null = null;
	let previewHighlightIndex: number | null = null;
	let previewBarRef: HTMLElement | undefined;
	let previewIndicatorRef: HTMLElement | undefined;
	let previewItemRefs: (HTMLElement | undefined)[] = [];
	let previewIndicatorX = 0;
	let previewIndicatorVisible = false;

	$: menuOrder = (() => {
		const order = parseHomeDataMenuOrderJson($profile_data?.menuOrder);
		if ($showNewLayout) {
			return finalizeNewLayoutMenuOrder(
				partitionNewLayoutMenuOrderIndices(
					appendMissingMenuOrderIndices(order, menuItemsNewLayout),
				).primary,
			);
		}
		if (!order.includes(MAIN_MENU_ORDER_INDEX_ADD_DEVICE))
			order.push(MAIN_MENU_ORDER_INDEX_ADD_DEVICE);
		return order;
	})();

	$: newLayoutPartition = $showNewLayout
		? partitionNewLayoutMenuOrderIndices(menuOrder)
		: { primary: [] as number[], assistant: NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT };

	$: currentOrder = [...menuOrder];
	$: currentNewLayoutPartition = $showNewLayout
		? partitionNewLayoutMenuOrderIndices(currentOrder)
		: { primary: [] as number[], assistant: NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT };

	$: customItemsAtEnd = $showNewLayout ? [] : [MAIN_MENU_ORDER_INDEX_ADD_DEVICE];
	$: pinnedNewLayoutIndices = $showNewLayout ? [NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT] : [];

	let items: Item[];
	$: items = ($showNewLayout ? newLayoutPartition.primary : menuOrder)
		.map((index) => ({
			id: ($showNewLayout ? menuItemsNewLayout : menuItems)[index]?.href ?? String(index),
			index,
		}))
		.filter((item) => !customItemsAtEnd.includes(item.index));

	let dndSettings: any;
	$: dndSettings = {
		items,
		flipDurationMs,
		dragDisabled,
		type: 'dashListRender',
		transformDraggedElement,
	};

	$: showMenuOrderHint = !$home_data?.hints?.[MENU_ORDER_HINT_KEY]?.closed;

	$: previewBackgroundImgSrc = getAppBackgroundSrc($profile_data, true) || undefined;
	$: previewBackgroundColorToken =
		!hasBackgroundColorImage($profile_data) && $profile_data?.layout?.backgroundColor
			? ($profile_data.layout.backgroundColor as string)
			: undefined;

	$: assistantMenuItem = menuItemsNewLayout[NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT];

	const updateHomeData = (newOrder: number[]) => {
		const newOrderStringify = JSON.stringify(newOrder);

		save_profile('menuOrder', newOrderStringify);
		profile_data.set({
			...$profile_data,
			menuOrder: newOrderStringify,
		});
	};

	const dismissMenuOrderHint = () => {
		const nextHints = {
			...$home_data?.hints,
			[MENU_ORDER_HINT_KEY]: {
				closed: true,
				ts: Date.now(),
				repeat: false,
			},
		};
		home_data.set({
			...$home_data,
			hints: nextHints,
		});
		save_home('hints', nextHints);
	};

	const transformDraggedElement = (draggedEl: HTMLElement) => {
		// Visual only — svelte-dnd-action owns focus / keyboard a11y.
		const child = draggedEl.firstElementChild as HTMLElement | null;
		if (!child) return;
		child.setAttribute('tabIndex', '-1');
		child.classList.add('on-drag');
	};

	const handleStartDrag = (item?: Item) => {
		previewHighlightIndex = item?.index ?? null;
		if (item && customItemsAtEnd.includes(item.index) && items[0].index !== item.index) {
			extractedItem = items[0];
			items = items.slice(1);
		} else {
			extractedItem = null;
		}
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		if (extractedItem) items.unshift(extractedItem);
		extractedItem = null;
		dragDisabled = true;
		// Keep preview highlight on the last moved item (full dock selected style).
		// Cleared only when another drag starts or the modal closes.
	};

	const clearPreviewHighlight = () => {
		previewHighlightIndex = null;
		previewIndicatorVisible = false;
		if (previewIndicatorRef) previewIndicatorRef.style.width = '';
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
		if ($showNewLayout) {
			currentOrder = finalizeNewLayoutMenuOrder(items.map((obj) => obj.index));
		}
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		items = [...e.detail.items];

		let newOrder = items.map((obj) => obj.index);
		for (const id of customItemsAtEnd) {
			if (!newOrder.includes(id)) newOrder.push(id);
		}

		if (extractedItem) newOrder.unshift(extractedItem.index);

		if ($showNewLayout) {
			newOrder = finalizeNewLayoutMenuOrder(newOrder);
		}

		currentOrder = newOrder;
		handleEndDrag();
	};

	const onOpenReorderMenuModal = () => {
		const order = [...menuOrder];
		currentOrder = order;
		const sourceIndices = $showNewLayout ? partitionNewLayoutMenuOrderIndices(order).primary : order;
		items = sourceIndices
			.map((index) => ({
				id: ($showNewLayout ? menuItemsNewLayout : menuItems)[index]?.href ?? String(index),
				index,
			}))
			.filter((item) => !customItemsAtEnd.includes(item.index));
		extractedItem = null;
		clearPreviewHighlight();
		showReorderMenuModal = true;
	};

	const onCloseReorderMenuModal = () => {
		clearPreviewHighlight();
		showReorderMenuModal = false;
	};

	const onSaveReorderMenu = () => {
		toast.open({
			type: 'success',
			title: $format('toast_menu_order_saved', {
				default: 'Menu order saved successfully.',
			}),
		});

		const orderToSave = $showNewLayout
			? finalizeNewLayoutMenuOrder(currentOrder)
			: [...currentOrder];
		currentOrder = orderToSave;
		onCloseReorderMenuModal();
		updateHomeData(orderToSave);
	};

	const previewItemIsHighlighted = (index: number) => previewHighlightIndex === index;

	/** Same black pill + edge glow as the live floating dock active indicator. */
	const updatePreviewIndicator = async () => {
		await tick();

		if (
			!showReorderMenuModal ||
			!$showNewLayout ||
			previewHighlightIndex === null ||
			!previewBarRef ||
			!previewIndicatorRef
		) {
			previewIndicatorVisible = false;
			if (previewIndicatorRef) previewIndicatorRef.style.width = '';
			return;
		}

		const slotIndex = currentNewLayoutPartition.primary.indexOf(previewHighlightIndex);
		const element = slotIndex >= 0 ? previewItemRefs[slotIndex] : undefined;
		if (!element?.offsetWidth) {
			previewIndicatorVisible = false;
			previewIndicatorRef.style.width = '';
			return;
		}

		// Inline preview uses flex cells; match the live dock where indicator ≈ cell width.
		previewIndicatorRef.style.width = `${element.offsetWidth}px`;

		const navBarWidth = previewBarRef.offsetWidth;
		const indicatorWidth = previewIndicatorRef.offsetWidth;
		if (!navBarWidth || !indicatorWidth) {
			previewIndicatorVisible = false;
			return;
		}

		const localCenter = element.offsetLeft + element.offsetWidth / 2;
		const unclampedIndicatorX = localCenter - indicatorWidth / 2 - PREVIEW_NAV_EDGE_INSET;
		const maxIndicatorX = navBarWidth - PREVIEW_NAV_EDGE_INSET * 2 - indicatorWidth;
		previewIndicatorX = Math.max(0, Math.min(unclampedIndicatorX, maxIndicatorX));
		previewIndicatorVisible = true;
	};

	$: {
		previewHighlightIndex;
		currentNewLayoutPartition.primary;
		showReorderMenuModal;
		$showNewLayout;
		void updatePreviewIndicator();
	}
</script>

<div class="d-flex gap-s justify-content-between align-items-center">
	<div>
		<Text styleType="body-medium" class="mb-xs">
			{capFirstChar($format('text_menu_items_app_setting_order', { default: 'Menu Items Order' }))}
		</Text>
		<Text styleType="body-small" class="text-color-muted">
			{$format('alert_menu_items_app_settings_reorder', {
				default: 'You can reorder the items in the Menu so that it is best suited for you.',
			})}
		</Text>
	</div>
	<ButtonV2 size="m" variant="secondary" on:click={onOpenReorderMenuModal}>
		{$format('modal_button_reorder', { default: 'Reorder' })}
	</ButtonV2>
</div>

<Modal
	title={$format('modal_button_reorder', { default: 'Reorder' })}
	showModal={showReorderMenuModal}
	on:close={onCloseReorderMenuModal}
>
	<svelte:fragment slot="content">
		{#if showMenuOrderHint}
			<AlertV2
				class="mb-l"
				dismissable
				heading={$format('alert_menu_items_order_heading', {
					default: 'First item on the Menu order',
				})}
				isHeadingBold
				message={$format('alert_menu_items_order_message', {
					default:
						'Keep in mind that the first item that you set will be the page that you land on when you open the app.',
				})}
				on:dismissable_click={dismissMenuOrderHint}
			/>
		{/if}

		<Container padding="0">
			<Text class="mb-s" styleType="h2">
				{$format('text_menu_items_rearangement_title', {
					default: 'Menu Items',
				})}
			</Text>

			{#if extractedItem}
				<CardGrid noPaddingBottom class="mb-s" widthUnit="%" sm={100}>
					<div class="select-none">
						<ListNumberCard size="s" cardNumber="01" editMode>
							<IconButton
								aria-label={$format('button_drag_to_reorder', {
									default: 'Drag to reorder',
								})}
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								disabled
							/>
							<Icon class="mr-s" icon={menuItems[extractedItem.index].icon} size="s" />
							<Text styleType="controls-small">
								{$format(menuItems[extractedItem.index].label, {
									default: menuItems[extractedItem.index].labelDefault,
								})}
							</Text>
						</ListNumberCard>
					</div>
				</CardGrid>
			{/if}

			<CardGrid
				class={$showNewLayout ? 'mb-s' : 'mb-m'}
				sm={100}
				gridGap="s"
				widthUnit="%"
				editMode={true}
				{dndSettings}
				on:consider={handleReorder}
				on:finalize={handleReorderEnd}
				modalContent
				noPaddingBottom
			>
				{#each items as item, i (item)}
					{@const menuItem = $showNewLayout
						? menuItemsNewLayout[item.index]
						: menuItems[item.index]}
					{#if menuItem}
						<div class:dnd-placeholder={item?.isDndShadowItem}>
							<ListNumberCard
								cardNumber={`${i + 1 + (extractedItem ? 1 : 0)}`.padStart(2, '0')}
								size="s"
								editMode
							>
								<IconButton
									aria-label={$format('button_drag_to_reorder', {
										default: 'Drag to reorder',
									})}
									class="mr-s"
									icon="fas fa-up-down-left-right"
									size="s"
									variant="primary"
									on:mousedown={(e) =>
										onStartDrag(e, true, () => handleStartDrag(item))}
									on:touchstart={(e) =>
										onStartDrag(e, true, () => handleStartDrag(item))}
									on:mouseup={(e) => onEndDrag(e, true, handleEndDrag)}
									on:touchend={(e) => onEndDrag(e, true, handleEndDrag)}
									dndHandle
								/>
								<Icon class="mr-s" icon={menuItem.icon} size="s" />
								<Text styleType="controls-small">
									{$format(menuItem.label, { default: menuItem.labelDefault })}
								</Text>
							</ListNumberCard>
						</div>
					{/if}
				{/each}
				{#if !$showNewLayout}
					<div class="mb-s">
						{#each customItemsAtEnd as lockedIndex}
							{@const lockedItem = menuItems[lockedIndex]}
							{@const lockedRowOrdinal =
								items.length +
								(extractedItem ? 1 : 0) +
								customItemsAtEnd.indexOf(lockedIndex) +
								1}
							{@const lockedRowCardNumber = `${lockedRowOrdinal}`.padStart(2, '0')}
							{#if lockedItem}
								<div class="select-none mb-s">
									<ListNumberCard size="s" cardNumber={lockedRowCardNumber} editMode>
										<IconButton
											aria-label={$format('button_drag_to_reorder', {
												default: 'Drag to reorder',
											})}
											class="mr-s"
											icon="fas fa-up-down-left-right"
											size="s"
											variant="primary"
											disabled
										/>
										<Icon class="mr-s" icon={lockedItem.icon} size="s" />
										<Text styleType="controls-small">
											{$format(lockedItem.label, {
												default: lockedItem.labelDefault,
											})}
										</Text>
									</ListNumberCard>
								</div>
							{/if}
						{/each}
					</div>
				{/if}
			</CardGrid>

			{#if $showNewLayout}
				<!-- Assistant and More are fixed chrome and cannot be reordered. -->
				{#each pinnedNewLayoutIndices as pinnedIndex}
					{@const pinnedItem = menuItemsNewLayout[pinnedIndex]}
					{#if pinnedItem}
						<ListNumberCard size="s" icon="fa-solid fa-map-pin" editMode class="mb-s">
							<IconButton
								aria-label={$format('button_drag_to_reorder', {
									default: 'Drag to reorder',
								})}
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								disabled
							/>
							<Icon class="mr-s" icon={pinnedItem.icon} size="s" />
							<Text styleType="controls-small" class="width-full">
								{$format(pinnedItem.label, { default: pinnedItem.labelDefault })}
							</Text>
						</ListNumberCard>
					{/if}
				{/each}
				<ListNumberCard size="s" icon="fa-solid fa-map-pin" editMode class="mb-m">
					<IconButton
						aria-label={$format('button_drag_to_reorder', {
							default: 'Drag to reorder',
						})}
						class="mr-s"
						icon="fas fa-up-down-left-right"
						size="s"
						variant="primary"
						disabled
					/>
					<Icon class="mr-s" icon={MORE_MENU_ICON} size="s" />
					<Text styleType="controls-small" class="width-full">
						{$format('title_more', { default: 'More' })}
					</Text>
				</ListNumberCard>
			{/if}

			<Text class="mb-s" styleType="h2">
				{$format('text_modal_menu_items_order_preview', {
					default: 'Preview',
				})}
			</Text>
			{#if $showNewLayout}
				<PreviewBox
					backgroundImgSrc={previewBackgroundImgSrc}
					backgroundColorToken={previewBackgroundColorToken}
					useHomeOverlay
				>
					<div class="menu-order-preview-dock-wrap" aria-hidden="true">
						<PillShapedDock
							inline
							highlightIconGrow
							showBackdrop={false}
							indicatorX={previewIndicatorX}
							indicatorVisible={previewIndicatorVisible}
							bind:barRef={previewBarRef}
							bind:indicatorRef={previewIndicatorRef}
						>
							{#each currentNewLayoutPartition.primary as itemIndex, slotIndex}
								{@const item = menuItemsNewLayout[itemIndex]}
								{@const isHighlighted = previewItemIsHighlighted(itemIndex)}
								{#if item}
									<div
										class="main-menu-item"
										class:is-active={isHighlighted}
										style={`--item-active-opacity:${isHighlighted ? 0.44 : 0};--item-active-scale:${isHighlighted ? 1.02 : 0};`}
										bind:this={previewItemRefs[slotIndex]}
									>
										<i class={`main-menu-item-icon ${item.icon}`}></i>
									</div>
								{/if}
							{/each}
							<span class="main-menu-divider"></span>
							{#if assistantMenuItem}
								<div class="main-menu-item">
									<i class={`main-menu-item-icon ${assistantMenuItem.icon}`}></i>
								</div>
							{/if}
							<div class="main-menu-item">
								<i class={`main-menu-item-icon ${MORE_MENU_ICON}`}></i>
							</div>
						</PillShapedDock>
					</div>
				</PreviewBox>
			{:else}
				<PreviewBox>
					<MainMenu isMobile useNewLayout={false} class="pt-0 pb-0">
						{@const docked = partitionMainMenuOrderIndices(currentOrder, menuItems)}
						<div class="menu-order-preview-dock">
							<div class="menu-order-preview-dock__primary">
								{#each docked.primary as itemIndex}
									{@const item = menuItems[itemIndex]}
									{#if item}
										<MenuItem
											isMobile
											useNewLayout={false}
											icon={item.icon}
											class={item.custom ? 'menu-item-custom-icon' : ''}
											label={$format(item.label, { default: item.labelDefault })}
										/>
									{/if}
								{/each}
							</div>
							{#if docked.footer.length}
								<div class="menu-order-preview-dock__footer">
									{#each docked.footer as itemIndex}
										{@const item = menuItems[itemIndex]}
										{#if item}
											<MenuItem
												isMobile
												useNewLayout={false}
												icon={item.icon}
												class={item.custom ? 'menu-item-custom-icon' : ''}
												label={$format(item.label, {
													default: item.labelDefault,
												})}
											/>
										{/if}
									{/each}
								</div>
							{/if}
						</div>
					</MainMenu>
				</PreviewBox>
			{/if}
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			size="m"
			on:click={onCloseReorderMenuModal}
		>
			{$format('button_cancel')}
		</ButtonV2>
		<ButtonV2
			variant="primary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap text-color-base"
			size="m"
			on:click={onSaveReorderMenu}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>

<style>
	.menu-order-preview-dock-wrap {
		display: block;
		width: 100%;
	}

	.menu-order-preview-dock {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: center;
		width: 100%;
		min-width: 0;
		gap: var(--spacing-m);
		padding-inline: var(--spacing-m);
		box-sizing: border-box;
	}

	.menu-order-preview-dock__primary,
	.menu-order-preview-dock__footer {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-m);
		flex: 0 0 auto;
		min-width: 0;
	}

	.menu-order-preview-dock__footer {
		margin-left: 0;
		padding-left: 0;
		border-left: none;
	}

	.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
	.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
		flex: 1 1 0 !important;
		min-width: 0 !important;
		width: auto !important;
		max-width: 96px !important;
		box-sizing: border-box !important;
		overflow: hidden;
	}

	.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout) .menu-item-text) {
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	@media screen and (max-width: 1123px) {
		.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
		.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
			max-width: 78px !important;
		}
	}

	@media screen and (max-width: 639px) {
		.menu-order-preview-dock {
			gap: var(--spacing-s);
			padding-inline: var(--spacing-s);
		}

		.menu-order-preview-dock__primary,
		.menu-order-preview-dock__footer {
			gap: var(--spacing-s);
		}

		.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
		.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
			max-width: 64px !important;
		}
	}
</style>
